/**
 * This file exposes frequently used validators methods.
 *
 * These methods are auto-imported in components and pages and can be used
 * directly in templates, <script setup> or anywhere in the Options API without
 * having to manually import them.
 */
import { DRIVER_LICENSE_FORMAT_VALIDATION_BY_REGION } from '~/constants/driver-license'
import {
  ADDRESS_PATTERN,
  MATTERPORT_PATTERN,
  PASSWORD_CASING_PATTERN,
  PASSWORD_DIGITS_CRITERIA_PATTERN,
  POSTAL_CODE_PATTERN,
  ZIPCODE_PATTERN,
} from '~/constants/validation'

import type { COUNTRY_SHORTS } from '~/constants'

/**
 * Helper to pass validation on empty values.
 *
 * @param value The value to check.
 * @param validate The validator function.
 * @returns The result of the validator function or true when empty.
 */
function passesWhenEmpty(value: Nullable<string | undefined>, validate: (value: string) => boolean) {
  if (value == null || value === '') {
    return true
  }

  return validate(value)
}

/**
 * Validates that a value is a valid address.
 *
 * @param value The address to validate.
 * @returns True if the value is a valid address, false otherwise.
 *
 * @example addressValidator('123 Main St.') // true
 */
export function addressValidator(value: string) {
  return passesWhenEmpty(value, (v) => ADDRESS_PATTERN.test(v))
}

/**
 * Validates that a value has at least one lowercase and uppercase letter.
 *
 * @param value The password to validate.
 * @returns True if the value has at least one lowercase and uppercase letter.
 *
 * @example passwordCasingCriteriaValidator('aA') // true
 */
export function passwordCasingCriteriaValidator(value: string) {
  return passesWhenEmpty(value, (v) => PASSWORD_CASING_PATTERN.test(v))
}

/**
 * Validates that a value has at least one number or special character.
 *
 * @param value The password to validate.
 * @returns True if the value has at least one number or special character.
 *
 * @example passwordNumberOrSymbolCriteriaValidator('foo1') // true
 */
export function passwordNumberOrSymbolCriteriaValidator(value: string) {
  return passesWhenEmpty(value, (v) => PASSWORD_DIGITS_CRITERIA_PATTERN.test(v))
}

/**
 * Validates that a value is a Matterport URL.
 *
 * @param value The URL to validate.
 * @returns True if the value is a Matterport URL.
 *
 * @example matterportUrlValidator('https://my.matterport.com/show/?m=1234') // true
 */
export function matterportUrlValidator(value: string) {
  return passesWhenEmpty(value, (v) => MATTERPORT_PATTERN.test(v))
}

/**
 * Validates that a value is a valid zip code (US).
 *
 * @param value The value to validate
 * @returns True if the value is a valid zip code.
 *
 * @example zipCodeValidator('12345') // true
 */
export function zipCodeValidator(value: string) {
  return passesWhenEmpty(value, (v) => ZIPCODE_PATTERN.test(v))
}

/**
 * Validates that a value is a valid postal code (CA).
 *
 * @param value The value to validate
 * @returns True if the value is a valid zip code.
 *
 * @example postalCodeValidator('A1A 1A1') // true
 */
export function postalCodeValidator(value: string) {
  return passesWhenEmpty(value, (v) => POSTAL_CODE_PATTERN.test(v))
}

/**
 * Validates that a value is a valid postal/zip code (US or CA).
 *
 * @param postalCode The postal code to validate.
 * @param countryCode The country code to validate against.
 * @returns True if the value is a valid postal code.
 *
 * @example postalCodeValidatorByCountryCode('123', 'US') // true
 */
export function postalCodeValidatorByCountryCode(postalCode: string, countryCode: string | COUNTRY_SHORTS) {
  if (countryCode === 'CA') {
    return postalCodeValidator(postalCode)
  }
  else if (countryCode === 'US') {
    return zipCodeValidator(postalCode)
  }

  return true
}

/**
 * Validates that a value is the same as another value in a case-insensitive manner.
 *
 * @param param The value to compare against
 * @returns True if the value is the same as the parameter in a case-insensitive manner.
 *
 * @example caseInsensitiveMatchValidator('foo', 'FOO') // true
 */
export function caseInsensitiveMatchValidator(param: string) {
  return (value: string) => value.toLowerCase() === param.toLowerCase()
}

/**
 * Validates that a value is a valid driver license number.
 *
 * @param licenseNumber The license number to validate.
 * @param countryCode The country code to validate against.
 * @param regionCode The region code to validate against.
 * @returns True if the value is a valid driver license number.
 *
 * @example driverLicenseValidatorByCountryCode('1234567890', 'US', 'CA') // true
 */
export function driverLicenseValidatorByCountryCode(licenseNumber: string, countryCode: COUNTRY_SHORTS, regionCode: string) {
  if (countryCode === 'CA') {
    return canadianDriverLicenseNumberValidator(regionCode, licenseNumber)
  }

  // We currently do not validate US driver licenses
  // Something to look into: https://gist.github.com/lipe-dev/4468b0fcc4c85acaa7bca9db534a9779
  return true
}

/**
 * Validates that a value is a valid Canadian driver license number.
 *
 * @param provinceCode The province code to validate against
 * @param licenseNumber The license number to validate
 * @returns True if the value is a valid Canadian driver license number.
 */
export function canadianDriverLicenseNumberValidator(provinceCode: string, licenseNumber: string) {
  if (!licenseNumber || !provinceCode || !DRIVER_LICENSE_FORMAT_VALIDATION_BY_REGION[provinceCode]) {
    return false
  }

  const format = DRIVER_LICENSE_FORMAT_VALIDATION_BY_REGION[provinceCode].format

  return format?.test(licenseNumber)
}
